import React, { useState, useEffect, useCallback } from 'react';

import { format, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import Layout from '../components/Layout';

import scheduleService from '../services/scheduleService';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  RefreshCw, Loader2, FileSpreadsheet, Eye, X,
  CalendarDays, CheckCircle, ClipboardList,
} from 'lucide-react';

function DailySettlementRecordPage() {
  const [records, setRecords] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchDate, setSearchDate] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, page_size: pageSize };
      if (searchDate) {
        params.start_date = searchDate;
        params.end_date = searchDate;
      }
      const data = await scheduleService.listDailySettlements(params);
      setRecords(data.items || []);
      setTotalItems(data.total || 0);
    } catch (err) {
      console.error('Failed to load settlements:', err);
      setError('加载结算记录失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDate]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleViewDetail = (record) => {
    setDetailRecord(record);
    setDetailOpen(true);
  };

  const wcColors = [
    '#fff1f2', '#fff7ed', '#fffbeb', '#f7fee7', '#ecfdf5',
    '#ecfeff', '#f0f9ff', '#f5f3ff', '#fdf4ff', '#fdf2f8',
  ];

  const escapeHtml = (val) => {
    if (val === null || val === undefined) return '';
    return String(val)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const buildHtmlTable = (rows, title, columns) => {
    const wcSet = [...new Set(rows.map((r) => r.work_center_name).filter(Boolean))];
    const wcColorMap = {};
    wcSet.forEach((wc, i) => { wcColorMap[wc] = wcColors[i % wcColors.length]; });

    let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><style>
  table { border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 11px; }
  th { background: #e5e7eb; border: 1px solid #9ca3af; padding: 6px; font-weight: bold; text-align: center; }
  td { border: 1px solid #d1d5db; padding: 5px; }
  .num { text-align: right; }
</style></head><body>
<h3>${escapeHtml(title)}</h3>
<table>
  <tr>${columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr>`;

    rows.forEach((row) => {
      const bg = wcColorMap[row.work_center_name] || '#ffffff';
      html += `  <tr style="background-color:${bg}">`;
      columns.forEach((c) => {
        const val = row[c.key] ?? '';
        const isNum = typeof val === 'number';
        html += `<td${isNum ? ' class="num"' : ''}>${escapeHtml(val)}</td>`;
      });
      html += '</tr>\n';
    });

    html += '</table></body></html>';
    return html;
  };

  const downloadHtmlXls = (html, filename) => {
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportSchedule = async (dateStr) => {
    try {
      const data = await scheduleService.exportSchedules({ start_date: dateStr, end_date: dateStr, type: 'schedule' });
      const rows = data.items || [];
      const columns = [
        { key: 'work_center_name', label: '加工中心' },
        { key: 'production_order_number', label: '工单号' },
        { key: 'client_name', label: '客户' },
        { key: 'finished_product_code', label: '产品编号' },
        { key: 'finished_product_name', label: '产品名称' },
        { key: 'step_name', label: '工序' },
        { key: 'assigned_quantity', label: '分配数量' },
        { key: 'completed_quantity', label: '已完成' },
        { key: 'scrapped_quantity', label: '报废' },
        { key: 'standard_time', label: '标准工时(分)' },
        { key: 'status', label: '状态' },
        { key: 'notes', label: '备注' },
      ];
      const html = buildHtmlTable(rows, `工作计划 ${dateStr}`, columns);
      downloadHtmlXls(html, `工作计划_${dateStr}.xls`);
    } catch (err) {
      setError('导出失败: ' + (err.message || ''));
    }
  };

  const handleExportSettlement = async (dateStr) => {
    try {
      const data = await scheduleService.exportSchedules({ start_date: dateStr, end_date: dateStr, type: 'settlement' });
      const rows = data.items || [];
      const columns = [
        { key: 'work_center_name', label: '加工中心' },
        { key: 'production_order_number', label: '工单号' },
        { key: 'client_name', label: '客户' },
        { key: 'finished_product_code', label: '产品编号' },
        { key: 'step_name', label: '工序' },
        { key: 'assigned_quantity', label: '分配数量' },
        { key: 'completed_quantity', label: '今日完成' },
        { key: 'scrapped_quantity', label: '今日报废' },
        { key: 'remaining_quantity', label: '剩余数量' },
        { key: 'rolled', label: '是否滚动' },
        { key: 'notes', label: '备注' },
      ];
      const html = buildHtmlTable(rows, `日结算结果 ${dateStr}`, columns);
      downloadHtmlXls(html, `结算结果_${dateStr}.xls`);
    } catch (err) {
      setError('导出失败: ' + (err.message || ''));
    }
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Layout activeTab="settings">
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">日结算记录</h1>
            <p className="text-muted-foreground">查询每日结算结果并导出报表</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadRecords} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="w-[200px]">
                <Input
                  type="date"
                  value={searchDate}
                  onChange={(e) => { setSearchDate(e.target.value); setPage(1); }}
                  placeholder="按日期筛选"
                />
              </div>
              {searchDate && (
                <Button variant="ghost" size="sm" onClick={() => { setSearchDate(''); setPage(1); }}>
                  清除
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              结算记录 ({totalItems})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                暂无结算记录
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>结算编号</TableHead>
                      <TableHead>日期</TableHead>
                      <TableHead>任务数</TableHead>
                      <TableHead>完成/报废/剩余</TableHead>
                      <TableHead>结算人</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map((record) => {
                      const dateStr = record.date ? format(parseISO(record.date), 'yyyy-MM-dd') : '-';
                      return (
                        <TableRow key={record._id}>
                          <TableCell className="font-mono text-xs">{record.settlement_id}</TableCell>
                          <TableCell>{dateStr}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {record.total_schedules || 0} 个任务
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {record.total_completed || 0} 完成 / {record.total_rolled || 0} 滚动
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-green-600 font-medium">{record.total_completed_qty || 0}</span>
                              <span className="text-gray-300">/</span>
                              <span className="text-red-500 font-medium">{record.total_scrapped_qty || 0}</span>
                              <span className="text-gray-300">/</span>
                              <span className="text-amber-600 font-medium">{record.total_remaining || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>{record.settled_by || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetail(record)}
                                title="查看详情"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExportSettlement(dateStr)}
                                title="导出结算结果"
                              >
                                <FileSpreadsheet className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExportSchedule(dateStr)}
                                title="导出工作计划"
                              >
                                <CalendarDays className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-muted-foreground">
                      第 {page} 页 / 共 {totalPages} 页（共 {totalItems} 项）
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >上一页</Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >下一页</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                结算详情 - {detailRecord?.settlement_id}
              </DialogTitle>
              <DialogDescription>
                {detailRecord?.date && format(parseISO(detailRecord.date), 'yyyy年MM月dd日', { locale: zhCN })}
                {' · '}结算人: {detailRecord?.settled_by || '-'}
              </DialogDescription>
            </DialogHeader>

            {detailRecord && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-lg font-bold text-gray-800">{detailRecord.total_schedules || 0}</div>
                    <div className="text-[10px] text-gray-500">任务总数</div>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <div className="text-lg font-bold text-green-700">{detailRecord.total_completed_qty || 0}</div>
                    <div className="text-[10px] text-green-600">完成总数</div>
                  </div>
                  <div className="bg-red-50 rounded p-2">
                    <div className="text-lg font-bold text-red-600">{detailRecord.total_scrapped_qty || 0}</div>
                    <div className="text-[10px] text-red-500">报废总数</div>
                  </div>
                  <div className="bg-amber-50 rounded p-2">
                    <div className="text-lg font-bold text-amber-700">{detailRecord.total_remaining || 0}</div>
                    <div className="text-[10px] text-amber-600">剩余总数</div>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">工单</TableHead>
                      <TableHead className="text-xs">产品</TableHead>
                      <TableHead className="text-xs">工序</TableHead>
                      <TableHead className="text-xs">加工中心</TableHead>
                      <TableHead className="text-xs text-right">分配</TableHead>
                      <TableHead className="text-xs text-right">完成</TableHead>
                      <TableHead className="text-xs text-right">报废</TableHead>
                      <TableHead className="text-xs text-right">剩余</TableHead>
                      <TableHead className="text-xs">状态</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailRecord.items || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs">{item.production_order_number}</TableCell>
                        <TableCell className="text-xs">{item.finished_product_code}</TableCell>
                        <TableCell className="text-xs">{item.step_name}</TableCell>
                        <TableCell className="text-xs">{item.work_center_name}</TableCell>
                        <TableCell className="text-xs text-right">{item.assigned_quantity}</TableCell>
                        <TableCell className="text-xs text-right text-green-600">{item.completed_quantity}</TableCell>
                        <TableCell className="text-xs text-right text-red-500">{item.scrapped_quantity}</TableCell>
                        <TableCell className="text-xs text-right text-amber-600">{item.remaining_quantity}</TableCell>
                        <TableCell className="text-xs">
                          {item.completed ? (
                            <Badge variant="default" className="text-[9px]">已完成</Badge>
                          ) : item.rolled ? (
                            <Badge variant="secondary" className="text-[9px]">已滚动</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px]">-</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default DailySettlementRecordPage;
